import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, X, ListChecks, CaretRight } from "@phosphor-icons/react";
import {
  WelcomeStep, BusinessStep, StrategyStep, BrandVoiceStep, ReadyStep,
} from "@/components/onboarding/OnboardingSteps";

const STEPS = [
  { id: "welcome" }, { id: "business" }, { id: "strategy" }, { id: "brand" }, { id: "ready" },
];

export const OnboardingWizard = () => {
  const { user, api, activeBusiness, businesses, loadBusinesses, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [bizName, setBizName] = useState("");
  const [bizIndustry, setBizIndustry] = useState("");
  const [yourName, setYourName] = useState("");
  const [yourTitle, setYourTitle] = useState("");
  const [whoYouServe, setWhoYouServe] = useState("");
  const [positioningStatement, setPositioningStatement] = useState("");
  const [whatMakesDifferent, setWhatMakesDifferent] = useState("");
  const [strategyName, setStrategyName] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const onboarding = user?.onboarding;
  const milestones = onboarding?.milestones || {};
  const planTier = user?.plan_tier || "free_beta";
  const isBeta = planTier === "free_beta";
  const isAdminLike = user?.plan_tier === "admin" || user?.account_role === "admin";
  const hasCoreSetup = !!(onboarding?.has_business && onboarding?.has_strategy_profile && onboarding?.has_business_profile);
  const isOnboardingDone = !!(localStorage.getItem(`onboarding_completed_${user?.user_id}`) || milestones.onboarding_completed || hasCoreSetup);
  const shouldShowChecklist = !!(user && onboarding && !isAdminLike && !isOnboardingDone);

  useEffect(() => {
    if (!user || !onboarding) return;
    if (localStorage.getItem(`onboarding_completed_${user.user_id}`)) return;
    if (isAdminLike) return;
    if (milestones.onboarding_dismissed) return;
    const autoOpenKey = `onboarding_auto_opened_${user.user_id}`;
    const hasAutoOpened = localStorage.getItem(autoOpenKey);
    const isTrulyEmpty = !onboarding.has_business && !onboarding.has_business_profile && !onboarding.has_strategy_profile;
    if (!hasAutoOpened && isTrulyEmpty) {
      setOpen(true);
      localStorage.setItem(autoOpenKey, "1");
    }
  }, [user, onboarding, milestones, isAdminLike]);

  const dismiss = () => {
    setOpen(false);
  };

  const markComplete = () => {
    if (user) localStorage.setItem(`onboarding_completed_${user.user_id}`, "1");
  };

  const markMilestone = async (milestoneId) => {
    try { await api.post(`/auth/onboarding/milestones/${milestoneId}`); await refreshUser(); } catch {}
  };

  const handleNotNow = async () => {
    await markMilestone("onboarding_dismissed");
    setOpen(false);
    toast.message("No problem — you can finish setup anytime from the checklist.");
  };

  const handleCreateBusiness = async () => {
    if (!bizName.trim()) { toast.error("Enter a business name"); return; }
    setSaving(true);
    try {
      await api.post("/businesses", { name: bizName.trim(), industry: bizIndustry.trim() });
      await loadBusinesses(); await refreshUser();
      toast.success(`Created "${bizName.trim()}"`); setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create business"); }
    finally { setSaving(false); }
  };

  const handleCreateStrategy = async () => {
    setSaving(true);
    try {
      await api.post("/strategy/profiles", {
        name: strategyName.trim() || "My Core Strategy",
        icp: "", positioning: "", offer: "",
        message_pillars: [], claims_to_avoid: [], proof_points: [],
      });
      await markMilestone("strategy_profile_created");
      toast.success("Strategy profile created"); setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create strategy profile"); }
    finally { setSaving(false); }
  };

  const handleSaveBrandVoice = async () => {
    setSaving(true);
    try {
      const payload = { your_name: yourName, your_title: yourTitle, who_you_serve: whoYouServe };
      if (positioningStatement.trim()) payload.positioning_statement = positioningStatement.trim();
      if (whatMakesDifferent.trim()) payload.differentiators = whatMakesDifferent.trim();
      await api.post("/profile", payload); await refreshUser();
      toast.success("Brand voice saved"); setStep(4);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleFinish = () => {
    markComplete(); markMilestone("onboarding_completed");
    setOpen(false); navigate("/chat?starter=1");
  };

  if (!user) return null;

  const showChecklist = !open && shouldShowChecklist;

  const stepContent = [
    <WelcomeStep key="welcome" user={user} isBeta={isBeta} onboarding={onboarding} businesses={businesses} onStart={setStep} />,
    <BusinessStep key="business" bizName={bizName} setBizName={setBizName} bizIndustry={bizIndustry} setBizIndustry={setBizIndustry} saving={saving} onSubmit={handleCreateBusiness} />,
    <StrategyStep key="strategy" strategyName={strategyName} setStrategyName={setStrategyName} saving={saving} onSubmit={handleCreateStrategy} showTemplatePicker={showTemplatePicker} setShowTemplatePicker={setShowTemplatePicker}
      onCloned={async () => { setShowTemplatePicker(false); await markMilestone("strategy_profile_created"); toast.success("Template added"); setStep(3); }}
      onTemplateCloned={async () => { setShowTemplatePicker(false); await markMilestone("strategy_profile_created"); toast.success("Template added"); setStep(3); }} />,
    <BrandVoiceStep key="brand" yourName={yourName} setYourName={setYourName} yourTitle={yourTitle} setYourTitle={setYourTitle}
      whoYouServe={whoYouServe} setWhoYouServe={setWhoYouServe} positioningStatement={positioningStatement} setPositioningStatement={setPositioningStatement}
      whatMakesDifferent={whatMakesDifferent} setWhatMakesDifferent={setWhatMakesDifferent} saving={saving} onSubmit={handleSaveBrandVoice} onSkip={() => setStep(4)} />,
    <ReadyStep key="ready" onboarding={onboarding} businesses={businesses} yourName={yourName} isBeta={isBeta} onFinish={handleFinish} />,
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
        <DialogContent className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="onboarding-wizard">
          <DialogTitle className="sr-only">Setup Wizard</DialogTitle>
          <div className="flex items-center gap-0 px-6 pt-5 pb-3">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center flex-1 last:flex-none">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  i < step ? "bg-[var(--success)] text-white" : i === step ? "bg-[#FF4400] text-white" : "bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border)]"
                }`}>{i < step ? <Check size={14} weight="bold" /> : i + 1}</div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1.5 rounded-full transition-colors ${i < step ? "bg-[var(--success)]" : "bg-[var(--border)]"}`} />}
              </div>
            ))}
          </div>
          <div className="px-6 pb-6 pt-2">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                {stepContent[step]}
              </motion.div>
            </AnimatePresence>
          </div>
          <button onClick={dismiss} className="absolute top-3 right-3 p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid="onboarding-dismiss-btn">
            <X size={16} />
          </button>
        </DialogContent>
      </Dialog>

      {showChecklist && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[var(--bg-card)] border border-[#FF4400]/30 shadow-lg rounded-xl px-2 py-2">
          <button
            onClick={() => { setOpen(true); }}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
            data-testid="onboarding-checklist-badge"
          >
            <ListChecks size={20} weight="fill" className="text-[#FF4400]" />
            <span className="text-sm font-medium text-[var(--text-primary)]">Finish Setup</span>
            <CaretRight size={14} className="text-[var(--text-muted)]" />
          </button>
          <button
            onClick={handleNotNow}
            className="text-xs px-2 py-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
            data-testid="onboarding-not-now-btn"
          >
            Not now
          </button>
        </motion.div>
      )}
    </>
  );
};
